import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Lightning } from "@phosphor-icons/react";
import { getSurveyForClick, submitSurvey } from "../lib/api";
import { Input } from "../components/ui/input";

function QuestionField({ q, value, onChange }) {
  if (q.type === "short_text") {
    return (
      <input
        data-testid={`q-${q.id}`}
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        required={q.required}
        className="w-full bg-[#121212] border border-white/10 rounded-none px-4 py-3 text-white focus:outline-none focus:border-[#ccff00]"
      />
    );
  }
  if (q.type === "yes_no") {
    return (
      <div className="flex gap-2">
        {["Yes", "No"].map((opt) => (
          <label
            key={opt}
            data-testid={`q-${q.id}-${opt.toLowerCase()}`}
            className={`flex-1 px-4 py-3 border cursor-pointer text-center transition-colors ${
              value === opt
                ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]"
                : "border-white/10 text-zinc-300 hover:border-white/30"
            }`}
          >
            <input
              type="radio"
              name={q.id}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              className="sr-only"
              required={q.required}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "multiple_choice") {
    return (
      <div className="space-y-2">
        {q.options.map((opt) => (
          <label
            key={opt}
            data-testid={`q-${q.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
            className={`flex items-center gap-3 px-4 py-3 border cursor-pointer transition-colors ${
              value === opt
                ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]"
                : "border-white/10 text-zinc-300 hover:border-white/30"
            }`}
          >
            <input
              type="radio"
              name={q.id}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              className="sr-only"
              required={q.required}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  }
  // checkboxes
  const arr = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-2">
      {q.options.map((opt) => {
        const checked = arr.includes(opt);
        return (
          <label
            key={opt}
            data-testid={`q-${q.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
            className={`flex items-center gap-3 px-4 py-3 border cursor-pointer transition-colors ${
              checked
                ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]"
                : "border-white/10 text-zinc-300 hover:border-white/30"
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) =>
                onChange(e.target.checked ? [...arr, opt] : arr.filter((x) => x !== opt))
              }
            />
            {opt}
          </label>
        );
      })}
    </div>
  );
}

export default function SurveyForm() {
  const { adSetId, platform, variantId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    getSurveyForClick(adSetId, platform, variantId)
      .then(setData)
      .catch((err) => setError(err.response?.data?.detail || "Failed to load survey"));
  }, [adSetId, platform, variantId]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const answersList = Object.entries(answers).map(([qid, value]) => ({
        question_id: qid,
        value,
      }));
      const res = await submitSurvey({
        survey_id: data.survey.id,
        ad_set_id: adSetId,
        platform,
        variant_id: variantId,
        answers: answersList,
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: contactPhone,
      });
      setDone(res);
      // Redirect after 1.5s so the user sees the "Thanks" flash
      setTimeout(() => {
        if (res.destination_url) window.location.href = res.destination_url;
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Submit failed");
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-[#0a0a0a] border border-white/10 p-8 text-center">
          <div className="label-mono mb-2 text-red-400">error</div>
          <div className="text-lg">{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="animate-pulse font-mono text-sm text-zinc-500">loading…</div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-[#0a0a0a] border border-white/10 p-10 text-center" data-testid="survey-thanks">
          <CheckCircle size={48} className="text-[#ccff00] mx-auto mb-4" weight="fill" />
          <h1 className="font-display text-3xl font-bold mb-2">Thanks!</h1>
          <p className="text-zinc-400">Taking you to the offer…</p>
        </div>
      </div>
    );
  }

  const { survey } = data;

  return (
    <div className="min-h-screen bg-[#050505] text-white py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-7 h-7 bg-[#ccff00] flex items-center justify-center text-black">
            <Lightning size={16} weight="fill" />
          </div>
          <span className="font-display font-bold tracking-tight">ADFORGE</span>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 p-8">
          <h1 className="font-display text-3xl font-bold mb-2" data-testid="survey-title">
            {survey.name}
          </h1>
          <p className="text-zinc-400 text-sm mb-8">
            Answer a few quick questions to continue to the offer.
          </p>

          <form onSubmit={submit} className="space-y-6" data-testid="survey-form">
            {survey.questions.map((q, i) => (
              <div key={q.id}>
                <div className="label-mono mb-2">
                  Q{i + 1}
                  {q.required && <span className="text-red-400"> *</span>}
                </div>
                <div className="font-display text-lg text-white mb-3">{q.label}</div>
                <QuestionField
                  q={q}
                  value={answers[q.id]}
                  onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
                />
              </div>
            ))}

            <div className="pt-6 border-t border-white/5 space-y-3">
              <div className="label-mono">contact</div>
              {survey.collect_name && (
                <Input
                  data-testid="contact-name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Your name"
                  className="rounded-none bg-[#121212] border-white/10 h-11"
                />
              )}
              {survey.collect_email && (
                <Input
                  data-testid="contact-email"
                  type="email"
                  required
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="Your email"
                  className="rounded-none bg-[#121212] border-white/10 h-11"
                />
              )}
              {survey.collect_phone && (
                <Input
                  data-testid="contact-phone"
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="Your phone"
                  className="rounded-none bg-[#121212] border-white/10 h-11"
                />
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-survey"
              className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold py-4 flex items-center justify-center gap-2 tracking-wide"
            >
              {submitting ? "Submitting…" : "Continue"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
